import React from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, ChevronRight, Play, Video, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Helper function to generate URL-friendly slug
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 100);
};

// For You Article Card - Newspaper style for both mobile and desktop
const ForYouArticleCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);
  
  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link 
      to={articleUrl} 
      onClick={handleClick}
      className="block group h-full cursor-pointer"
    >
      <div className="flex flex-col h-full">
        <div className="aspect-[16/10] bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800">
              <Sparkles className="h-8 w-8 text-gray-300" />
            </div>
          )}
        </div>
        <div className="pt-2 flex-1 flex flex-col">
          <h4 className="text-xs sm:text-sm font-normal text-gray-800 dark:text-gray-100 leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2 flex-1">
            {title}
          </h4>
          <p className="text-[9px] sm:text-[10px] text-gray-400 mt-1">
            {article.source || 'YegoNews'}
          </p>
        </div>
      </div>
    </Link>
  );
};

// For You Section - Personalized/Trending articles
export const ForYouSection = ({ 
  articles = [], 
  title = "For You",
  className = "",
  onArticleClick
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <section className={`${className}`}>
      {/* Newspaper style header - same for mobile and desktop */}
      <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 border-b-2 border-gray-900 dark:border-gray-100">
        <h3 className="text-[10px] sm:text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5 sm:gap-2">
          <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-purple-600" />
          {title}
        </h3>
        <Link to="/for-you" className="text-[10px] sm:text-[11px] text-gray-500 hover:text-emerald-600 flex items-center gap-1 uppercase tracking-wide">
          {isKinyarwanda ? 'Byose' : 'View all'}
          <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
        </Link>
      </div>
      
      {/* Grid layout - same for mobile and desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {articles.slice(0, 12).map((article, index) => (
          <ForYouArticleCard key={article.id || index} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
        ))}
      </div>
    </section>
  );
};

// Sponsored Article Card - Newspaper style
const SponsoredArticleCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);
  
  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link 
      to={articleUrl} 
      onClick={handleClick}
      className="block group h-full cursor-pointer"
    >
      <div className="flex flex-col h-full">
        <div className="aspect-[16/10] bg-gray-100 dark:bg-gray-800 overflow-hidden flex-shrink-0">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800">
              <Megaphone className="h-8 w-8 text-gray-300" />
            </div>
          )}
        </div>
        <div className="pt-2 flex-1 flex flex-col">
          <h4 className="text-xs sm:text-sm font-normal text-gray-800 dark:text-gray-100 leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2 flex-1">
            {title}
          </h4>
          <p className="text-[9px] sm:text-[10px] text-gray-400 mt-1">
            {article.source || 'YegoNews'}
          </p>
        </div>
      </div>
    </Link>
  );
};

// Sponsored Section - Newspaper style grid
export const SponsoredArticlesSection = ({ 
  articles = [], 
  title = "Sponsored",
  className = "",
  onArticleClick
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <section className={`${className}`}>
      {/* Newspaper style header */}
      <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 border-b-2 border-gray-900 dark:border-gray-100">
        <h3 className="text-[10px] sm:text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5 sm:gap-2">
          <Megaphone className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-600" />
          {isKinyarwanda ? "Kwamamaza" : title}
        </h3>
        <Link to="/sponsored" className="text-[10px] sm:text-[11px] text-gray-500 hover:text-emerald-600 flex items-center gap-1 uppercase tracking-wide">
          {isKinyarwanda ? 'Byose' : 'View all'}
          <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
        </Link>
      </div>
      
      {/* Grid layout - same for mobile and desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {articles.slice(0, 12).map((article, index) => (
          <SponsoredArticleCard key={article.id || index} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
        ))}
      </div>
    </section>
  );
};

// Announcement Item (full text, no truncation)
const AnnouncementItem = ({ announcement, isKinyarwanda }) => {
  const title = isKinyarwanda && announcement.title_rw 
    ? announcement.title_rw 
    : (announcement.title_en || announcement.title);
  const description = isKinyarwanda && announcement.description_rw 
    ? announcement.description_rw 
    : (announcement.description_en || announcement.description);
  
  const handleClick = () => {
    if (announcement.link) {
      window.open(announcement.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div 
      onClick={handleClick}
      className="cursor-pointer group"
    >
      <div className="flex gap-2.5 p-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg transition-colors">
        {/* Icon */}
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
          <Megaphone className="h-3.5 w-3.5 text-white" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-xs font-medium text-gray-900 dark:text-white leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
            {title}
          </h4>
          {description && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Announcements Section - Simple list
export const AnnouncementsListSection = ({ 
  announcements = [], 
  title = "Amatangazo amenyesha",
  className = ""
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  if (!announcements || announcements.length === 0) {
    return null;
  }

  return (
    <section className={`bg-gray-50 dark:bg-gray-800/50 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-gray-900 dark:text-white font-bold text-sm flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-emerald-500" />
          {title}
        </h3>
      </div>
      
      {/* Vertical List */}
      <div className="p-2.5 space-y-0.5">
        {announcements.slice(0, 5).map((announcement, index) => (
          <AnnouncementItem 
            key={announcement.id || index} 
            announcement={announcement} 
            isKinyarwanda={isKinyarwanda}
          />
        ))}
      </div>
    </section>
  );
};

// Two-Column Card - SMALLER size
const TwoColumnCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;
  
  const isExternal = article.is_fetched || (article.link && !article.is_user_created);
  
  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
      return;
    }
    if (isExternal && article.link) {
      e.preventDefault();
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Link 
      to={articleUrl} 
      onClick={handleClick}
      className="group h-full cursor-pointer"
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow h-full flex flex-col">
        {/* Image - Smaller aspect ratio */}
        <div className="aspect-[16/10] bg-gray-200 dark:bg-gray-800 overflow-hidden flex-shrink-0">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <span className="text-gray-400 text-[10px]">No image</span>
            </div>
          )}
        </div>
        
        {/* Title - Full text, smaller */}
        <div className="p-1.5 flex-1">
          <h4 className="text-[10px] font-medium text-gray-900 dark:text-white leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-3">
            {title}
          </h4>
        </div>
      </div>
    </Link>
  );
};

// Two-Column Section (Sports) - Horizontal scroll of 2x2 grids on mobile, vertical grid on desktop
export const TwoColumnScrollSection = ({ 
  articles = [], 
  title = "Imikino",
  titleColor = "text-pink-500",
  showBadge = true,
  badgeText = "Live",
  className = "",
  onArticleClick
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  if (!articles || articles.length === 0) {
    return null;
  }

  // Group articles into chunks of 4 for mobile 2x2 grid pages
  const chunkedArticles = [];
  for (let i = 0; i < articles.length; i += 4) {
    chunkedArticles.push(articles.slice(i, i + 4));
  }

  return (
    <section className={`${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <h3 className={`font-bold text-sm ${titleColor}`}>{title}</h3>
          {showBadge && (
            <span className="px-1.5 py-0.5 bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 text-[10px] rounded-full">
              {badgeText}
            </span>
          )}
        </div>
        <Link to="/category/sports" className="text-xs text-gray-500 hover:text-emerald-600 flex items-center gap-0.5">
          {isKinyarwanda ? 'Byose' : 'View all'}
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      
      {/* Mobile: Horizontal scroll of 2x2 grid pages */}
      <div className="sm:hidden overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
        <div className="flex gap-3" style={{ width: 'max-content' }}>
          {chunkedArticles.slice(0, 3).map((chunk, chunkIndex) => (
            <div key={chunkIndex} className="w-[calc(100vw-32px)] flex-shrink-0 snap-start">
              <div className="grid grid-cols-2 gap-2">
                {chunk.map((article, index) => (
                  <TwoColumnCard key={article.id || `${chunkIndex}-${index}`} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Desktop: Vertical scrollable grid */}
      <div className="hidden sm:block max-h-[320px] overflow-y-auto pr-1 scrollbar-thin">
        <div className="grid grid-cols-3 lg:grid-cols-4 gap-2">
          {articles.slice(0, 12).map((article, index) => (
            <TwoColumnCard key={article.id || index} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ VIDEO SECTION ============

// Video Card - Shows play button overlay
const VideoCard = ({ article, isKinyarwanda, onArticleClick }) => {
  const title = isKinyarwanda && article.title_rw ? article.title_rw : (article.title_en || article.title);
  const articleUrl = `/news/${generateSlug(title)}`;

  const handleClick = (e) => {
    if (onArticleClick) {
      e.preventDefault();
      onArticleClick(article);
    }
  };

  return (
    <Link to={articleUrl} onClick={handleClick} className="group h-full cursor-pointer">
      <div className="bg-white dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow h-full flex flex-col">
        {/* Video Thumbnail with Play Button */}
        <div className="relative aspect-video bg-gray-900 overflow-hidden flex-shrink-0">
          {article.image_url || article.thumbnail_url ? (
            <img
              src={article.thumbnail_url || article.image_url}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <Video className="h-10 w-10 text-gray-600" />
            </div>
          )}
          
          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
              <Play className="h-5 w-5 text-gray-900 ml-0.5" fill="currentColor" />
            </div>
          </div>
          
          {/* Duration badge if available */}
          {article.video_duration && (
            <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-[10px] rounded">
              {article.video_duration}
            </div>
          )}
        </div>
        
        {/* Title */}
        <div className="p-2 flex-1">
          <h4 className="text-xs font-medium text-gray-900 dark:text-white leading-snug group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2">
            {title}
          </h4>
          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-500">
            <span>{article.source || 'YegoNews'}</span>
            {article.views && (
              <>
                <span>•</span>
                <span>{article.views} views</span>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

// Video Section - YEGOtv - Horizontal scroll of 2x2 grids on mobile, vertical grid on desktop
export const VideoSection = ({ 
  articles = [], 
  title = "YEGOtv",
  className = "",
  onArticleClick
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  // Filter only articles that have videos
  const videoArticles = articles.filter(a => a.video_url || a.has_video);

  if (!videoArticles || videoArticles.length === 0) {
    return null;
  }

  // Group articles into chunks of 4 for mobile 2x2 grid pages
  const chunkedArticles = [];
  for (let i = 0; i < videoArticles.length; i += 4) {
    chunkedArticles.push(videoArticles.slice(i, i + 4));
  }

  return (
    <section className={`${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
            <Video className="h-5 w-5 text-red-500" />
            {title}
          </h3>
          <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
            Live
          </span>
        </div>
        <Link to="/videos" className="text-sm text-gray-500 hover:text-emerald-600 flex items-center gap-1">
          {isKinyarwanda ? 'Byose' : 'View all'}
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      
      {/* Mobile: Horizontal scroll of 2x2 grid pages */}
      <div className="sm:hidden overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
        <div className="flex gap-3" style={{ width: 'max-content' }}>
          {chunkedArticles.slice(0, 3).map((chunk, chunkIndex) => (
            <div key={chunkIndex} className="w-[calc(100vw-32px)] flex-shrink-0 snap-start">
              <div className="grid grid-cols-2 gap-2">
                {chunk.map((article, index) => (
                  <VideoCard key={article.id || `${chunkIndex}-${index}`} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Desktop: Vertical scrollable grid */}
      <div className="hidden sm:block max-h-[340px] overflow-y-auto pr-1 scrollbar-thin">
        <div className="grid grid-cols-3 lg:grid-cols-4 gap-3">
          {videoArticles.slice(0, 12).map((article, index) => (
            <VideoCard key={article.id || index} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Bottom Sponsored Section - Horizontal scroll of 2x2 grids on mobile, vertical grid on desktop
export const BottomSponsoredSection = ({ 
  articles = [], 
  title = "Sponsored",
  className = "",
  onArticleClick
}) => {
  const { language } = useLanguage();
  const isKinyarwanda = language === 'rw';

  if (!articles || articles.length === 0) {
    return null;
  }

  // Group articles into chunks of 4 for mobile 2x2 grid pages
  const chunkedArticles = [];
  for (let i = 0; i < articles.length; i += 4) {
    chunkedArticles.push(articles.slice(i, i + 4));
  }

  return (
    <section className={`${className}`}>
      {/* Header - Same style as top sponsored */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
            <Megaphone className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">
            {isKinyarwanda ? "Kwamamaza" : title}
          </h3>
        </div>
        <Link to="/sponsored" className="text-sm text-gray-500 hover:text-emerald-600 flex items-center gap-1">
          {isKinyarwanda ? 'Byose' : 'View all'}
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      
      {/* Mobile: Horizontal scroll of 2x2 grid pages */}
      <div className="sm:hidden overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
        <div className="flex gap-3" style={{ width: 'max-content' }}>
          {chunkedArticles.slice(0, 3).map((chunk, chunkIndex) => (
            <div key={chunkIndex} className="w-[calc(100vw-32px)] flex-shrink-0 snap-start">
              <div className="grid grid-cols-2 gap-2">
                {chunk.map((article, index) => (
                  <SponsoredArticleCard key={article.id || `${chunkIndex}-${index}`} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Desktop: Vertical scrollable grid */}
      <div className="hidden sm:block max-h-[340px] overflow-y-auto pr-1 scrollbar-thin">
        <div className="grid grid-cols-3 lg:grid-cols-4 gap-3">
          {articles.slice(0, 12).map((article, index) => (
            <SponsoredArticleCard key={article.id || index} article={article} isKinyarwanda={isKinyarwanda} onArticleClick={onArticleClick} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default {
  ForYouSection,
  SponsoredArticlesSection,
  AnnouncementsListSection,
  TwoColumnScrollSection,
  VideoSection,
  BottomSponsoredSection
};
